/**
 * h5/src/admin/auditLog.ts
 * F.6 — Admin Audit Log（Ring Buffer 200 条 + telemetry 同步）
 *
 * L5 防护：每次 unlock/lock/factor_fail/secret_inject/reconnect 都 push
 * 一条带 timestamp+event+actor 的记录，并同步发送 telemetry event。
 */

// ─── 类型定义 ────────────────────────────────────────────────────────────────

export type AuditEventType =
  | 'unlock'
  | 'lock'
  | 'factor_fail'
  | 'secret_inject'
  | 'reconnect'
  | 'attestation_skip'
  | 'pin_cooldown'
  | 'ttl_expired'
  | 'pin_changed'
  | 'cache_cleared'
  | 'network_config_changed'
  | 'network_config_reset'
  | 'window_mode_changed'
  | 'machine_code_set'
  | 'keyt_regen'
  | 'autostart_changed'
  | 'update_policy_saved'
  | 'serial_ops_saved'

export interface AuditEntry {
  id: string
  timestamp: number       // epoch ms
  event: AuditEventType
  actor: string           // 'admin' | 'system' | 'auto'
  detail?: Record<string, unknown>
}

// ─── Ring Buffer 常量 ────────────────────────────────────────────────────────

export const AUDIT_RING_MAX = 200

// ─── 内部状态 ────────────────────────────────────────────────────────────────

const _ring: AuditEntry[] = []
let _seq = 0

// ─── 核心 API ────────────────────────────────────────────────────────────────

/**
 * 推送一条 audit 记录
 * - 容量超过 AUDIT_RING_MAX 时移除最旧记录（ring buffer 语义）
 * - 同步发送 telemetry event（L5 防护）
 */
export function pushAudit(
  event: AuditEventType,
  actor: 'admin' | 'system' | 'auto' = 'system',
  detail?: Record<string, unknown>,
): AuditEntry {
  const entry: AuditEntry = {
    id: `audit-${Date.now()}-${++_seq}`,
    timestamp: Date.now(),
    event,
    actor,
    detail,
  }

  _ring.push(entry)

  // Ring buffer：超过上限时丢弃最旧
  if (_ring.length > AUDIT_RING_MAX) {
    _ring.shift()
  }

  // L5：同步发送 telemetry（fire-and-forget，不阻塞）
  _sendTelemetry(entry)

  return entry
}

/**
 * 获取最近 n 条审计记录（默认 10 条）
 */
export function getRecentAudit(n = 10): AuditEntry[] {
  return _ring.slice(-n)
}

/**
 * 获取完整 ring buffer 副本（仅供 UI 展示 / 测试）
 */
export function getAllAudit(): AuditEntry[] {
  return [..._ring]
}

/**
 * 清空 ring buffer（仅供测试用）
 * @internal
 */
export function _resetAuditForTest(): void {
  _ring.splice(0, _ring.length)
  _seq = 0
}

// ─── Telemetry 同步（L5 防护） ────────────────────────────────────────────────

/**
 * 将 audit entry 作为 telemetry event 发送
 * 使用动态 import 避免循环依赖
 * 失败时静默（不影响 audit ring 本身）
 */
function _sendTelemetry(entry: AuditEntry): void {
  // 动态 import 避免循环依赖
  import('@/composables/useTelemetry')
    .then(({ getTelemetry }) => {
      // 使用扩展的 sub_type（admin_audit）
      // useTelemetry 的 TelemetrySubType 不含此类型，
      // 这里直接调用底层 track 并强制转型（audit 事件不经过 flush 批量）
      const telemetry = getTelemetry()
      // 注：telemetry.track 的 sub_type 是 string union，
      // 这里通过 as any 传递 admin_audit，auditor 侧自行过滤
      ;(telemetry as { track: (s: string, p: Record<string, unknown>) => void }).track(
        'admin_audit',
        {
          audit_event: entry.event,
          actor: entry.actor,
          audit_id: entry.id,
          ...(entry.detail ?? {}),
        },
      )
    })
    .catch(() => {
      // 静默失败，audit ring 本身不受影响
    })
}
